import { mergeAttributes, Node } from '@tiptap/core';
import { NodeViewWrapper, ReactNodeViewRenderer } from '@tiptap/react';
import {
  Button,
  ButtonGroup,
  Icon,
  IconButton,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  Popover,
  PopoverBody,
  PopoverCloseButton,
  PopoverContent,
  PopoverTrigger,
  Tabs,
  TabList,
  TabPanels,
  Tab,
  TabPanel,
  Input,
  Flex,
  PopoverHeader,
  FormControl,
  FormErrorMessage,
  Spinner,
  Text,
} from '@chakra-ui/react';
import { ImageIcon, MoreHorizontalIcon } from 'lucide-react';
import { useCallback, useState } from 'react';
import { useDukeTiptapEditor } from '../../useDukeTiptapEditor';

const imageUrlRegex = /^(ftp|http|https):\/\/[^ "]+$/;

function ImageDialogReactComponent(props) {
  const { uploadFn } = useDukeTiptapEditor();
  const [imageUrl, setImageUrl] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [file, setFile] = useState<File | null>(null);
  const [uploading, setUploading] = useState<boolean>(false);
  const [uploadFailed, setUploadFailed] = useState<boolean>(false);

  const handleFileChange = useCallback(
    (file: File | null) => {
      if (file === null) {
        return;
      }

      async function upload(file: File) {
        setUploading(true);

        if (uploadFn !== undefined) {
          try {
            const { filename, fileDrn } = await uploadFn(file);
            const currentPos = props.getPos();
            props.deleteNode();
            props.editor.commands.setImage({ src: `/files/${filename}`, fileDrn }, currentPos);
          } catch (e) {
            setUploadFailed(true);
          } finally {
            setUploading(false);
          }
        }
      }

      upload(file);
    },
    [props, uploadFn],
  );

  if (!props.editor.options.editable) {
    return null;
  }

  const validateUrl = (url: string) => {
    return imageUrlRegex.test(url);
  };

  const handleEmbedImage = () => {
    if (!validateUrl(imageUrl)) {
      setError('Invalid URL. Please enter a valid image URL.');
      return;
    }
    setError(null);
    const currentPos = props.getPos();
    props.deleteNode();
    props.editor.commands.setImage({ src: imageUrl }, currentPos);
  };

  return (
    <ButtonGroup size="xl" isAttached variant="outline" colorScheme="gray" w="full">
      <Popover matchWidth placement="bottom-start">
        <PopoverTrigger>
          <Button flex={1} justifyContent="start" leftIcon={<ImageIcon />}>
            Add image
          </Button>
        </PopoverTrigger>
        <PopoverContent w="full">
          <PopoverCloseButton />
          <PopoverHeader>Select how you want to add an image</PopoverHeader>
          <PopoverBody>
            <Tabs isFitted>
              <TabList>
                {uploadFn !== undefined && <Tab>Upload</Tab>}
                <Tab>Embed link</Tab>
                {/* <Tab>GIPHY</Tab> */}
              </TabList>
              <TabPanels>
                {uploadFn !== undefined && (
                  <TabPanel>
                    <Flex direction="column" gap={4} justifyContent="center">
                      {uploading ? (
                        <Flex
                          direction="column"
                          gap={4}
                          justifyContent="center"
                          alignItems="center"
                        >
                          <Spinner size="md" color="brand.500" />
                          <Text size="sm">Uploading...</Text>
                        </Flex>
                      ) : (
                        <>
                          <FormControl isInvalid={uploadFailed}>
                            <input
                              type="file"
                              accept={'image/*'}
                              onChange={(e) => setFile(e.target.files?.[0] ?? null)}
                            />
                            <FormErrorMessage>Failed to upload image</FormErrorMessage>
                          </FormControl>
                          <Button onClick={() => handleFileChange(file)} size="sm">
                            Upload
                          </Button>
                        </>
                      )}
                    </Flex>
                  </TabPanel>
                )}
                <TabPanel>
                  <Flex direction="column" gap={4}>
                    <FormControl isInvalid={!!error}>
                      <Input
                        placeholder="Image link"
                        autoComplete="off"
                        spellCheck="false"
                        autoFocus
                        onChange={(e) => setImageUrl(e.target.value)}
                      />
                      <FormErrorMessage>{error}</FormErrorMessage>
                    </FormControl>
                    <Button size="sm" onClick={handleEmbedImage}>
                      Embed image
                    </Button>
                  </Flex>
                </TabPanel>
                {/* <TabPanel>
                  <Flex direction="column" gap={4}>
                    <Input placeholder="GIPHY link" />
                    <Button>Embed GIPHY</Button>
                  </Flex>
                </TabPanel> */}
              </TabPanels>
            </Tabs>
          </PopoverBody>
        </PopoverContent>
      </Popover>
      <Menu>
        <MenuButton
          as={IconButton}
          icon={<Icon as={MoreHorizontalIcon} />}
          aria-label="image dialog options"
        />
        <MenuList>
          <MenuItem onClick={props.deleteNode}>Delete</MenuItem>
        </MenuList>
      </Menu>
    </ButtonGroup>
  );
}

function ImageDialogTiptapWrapper(props) {
  return (
    <NodeViewWrapper>
      <ImageDialogReactComponent {...props} />
    </NodeViewWrapper>
  );
}

export const ImageDialog = Node.create({
  name: 'image-dialog',

  draggable: true,

  group: 'block',

  atom: true,

  parseHTML() {
    return [
      {
        tag: 'image-dialog',
      },
    ];
  },

  renderHTML({ HTMLAttributes }) {
    return ['image-dialog', mergeAttributes(HTMLAttributes)];
  },

  addNodeView() {
    return ReactNodeViewRenderer((props) => <ImageDialogTiptapWrapper {...props} />);
  },
});
